import React, { useState } from 'react';
import { Box, Button, Collapse, Link, Typography, Tooltip, Chip } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';

export interface Comparable {
  id?: string;
  address: string;
  salePrice: number;
  pricePerSqft: number;
  saleDate: string;
  distanceMiles: number;
  zillowUrl?: string;
  // Enhanced fields from RentCast
  squareFeet?: number;
  bedrooms?: number;
  bathrooms?: number;
  city?: string;
  state?: string;
  propertyType?: string;
  // Filtering metadata (TASK-106)
  daysAgo?: number;
  status?: 'used' | 'excluded';
  exclusionReason?: string;
}

interface ComparablesSectionProps {
  comps: Comparable[];
  /** Whether comps are from RentCast (verified) vs mock data */
  isVerified?: boolean;
}

/**
 * ComparablesSection - Expandable table showing comparable sales
 *
 * Supports both basic comps (address, price, sqft, date, distance)
 * and enhanced RentCast comps (with beds/baths, Zillow links).
 * Shows filtering status (used vs excluded) when available.
 */
export const ComparablesSection: React.FC<ComparablesSectionProps> = ({
  comps,
  isVerified = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Format date in Mountain Time
  const formatDate = (dateString: string): string => {
    try {
      // Ensure the timestamp is treated as UTC by appending 'Z' if not present
      const utcString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
      const date = new Date(utcString);

      // Validate date
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateString);
        return dateString;
      }

      return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Denver',
        month: 'short',
        year: '2-digit',
      }).format(date);
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Format beds/baths display
  const formatBedsBaths = (beds?: number, baths?: number): string | null => {
    if (beds === undefined && baths === undefined) return null;
    const bedsStr = beds !== undefined ? `${beds}bd` : '';
    const bathsStr = baths !== undefined ? `${baths}ba` : '';
    return [bedsStr, bathsStr].filter(Boolean).join('/');
  };

  // Show "No Comps Available" when empty
  if (comps.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="caption"
          sx={{
            color: '#8b949e',
            fontSize: '0.8rem',
            fontStyle: 'italic',
          }}
        >
          No Comps Available
        </Typography>
      </Box>
    );
  }

  // Separate used and excluded comps
  const usedComps = comps.filter(c => c.status === 'used' || !c.status);
  const excludedComps = comps.filter(c => c.status === 'excluded');
  const hasFilteringInfo = comps.some(c => c.status !== undefined);

  // Build button label
  const buttonLabel = hasFilteringInfo
    ? `View ${usedComps.length} Used / ${comps.length} Total Comps`
    : `View ${comps.length} Comps`;

  return (
    <Box sx={{ mt: 2 }}>
      <Button
        size="small"
        onClick={() => setExpanded(!expanded)}
        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          color: isVerified ? '#a78bfa' : '#4ade80',
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8rem',
          p: 0,
          '&:hover': { bgcolor: 'transparent' },
        }}
      >
        {buttonLabel} {isVerified && '(RentCast)'}
      </Button>

      <Collapse in={expanded}>
        <Box
          sx={{
            mt: 1,
            bgcolor: '#21262d',
            border: '1px solid #30363d',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* Used comps section */}
          {usedComps.map((comp, index) => (
            <CompRow
              key={comp.id || `used-${index}`}
              comp={comp}
              index={index}
              isLast={index === usedComps.length - 1 && excludedComps.length === 0}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              formatBedsBaths={formatBedsBaths}
            />
          ))}

          {/* Excluded comps section with header */}
          {excludedComps.length > 0 && (
            <>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  bgcolor: '#161b22',
                  borderTop: usedComps.length > 0 ? '1px solid #30363d' : 'none',
                  borderBottom: '1px solid #30363d',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: '#8b949e', fontSize: '0.65rem', fontWeight: 500 }}
                >
                  {excludedComps.length} Excluded Comp{excludedComps.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
              {excludedComps.map((comp, index) => (
                <CompRow
                  key={comp.id || `excluded-${index}`}
                  comp={comp}
                  index={index}
                  isLast={index === excludedComps.length - 1}
                  formatCurrency={formatCurrency}
                  formatDate={formatDate}
                  formatBedsBaths={formatBedsBaths}
                  isExcluded
                />
              ))}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

interface CompRowProps {
  comp: Comparable;
  index: number;
  isLast: boolean;
  formatCurrency: (value: number) => string;
  formatDate: (dateString: string) => string;
  formatBedsBaths: (beds?: number, baths?: number) => string | null;
  isExcluded?: boolean;
}

const CompRow: React.FC<CompRowProps> = ({
  comp,
  index,
  isLast,
  formatCurrency,
  formatDate,
  formatBedsBaths,
  isExcluded = false,
}) => {
  const bedsBaths = formatBedsBaths(comp.bedrooms, comp.bathrooms);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 1.5,
        py: 1,
        borderBottom: !isLast ? '1px solid #30363d' : 'none',
        '&:hover': { bgcolor: '#161b22' },
        opacity: isExcluded ? 0.6 : 1,
      }}
    >
      {/* Left side: Status icon, Address, Date, Beds/Baths */}
      <Box sx={{ flex: 1, minWidth: 0, mr: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        {/* Status indicator */}
        {comp.status && (
          <Box sx={{ mt: 0.25 }}>
            {comp.status === 'used' ? (
              <CheckIcon sx={{ fontSize: '0.9rem', color: '#4ade80' }} />
            ) : (
              <Tooltip title={comp.exclusionReason || 'Excluded from ARV'} arrow placement="top">
                <CloseIcon sx={{ fontSize: '0.9rem', color: '#f87171', cursor: 'help' }} />
              </Tooltip>
            )}
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ color: '#f0f6fc', fontSize: '0.75rem', fontWeight: 500 }}>
            {comp.zillowUrl ? (
              <Link
                href={comp.zillowUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#60a5fa',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {comp.address}
              </Link>
            ) : (
              comp.address
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, color: '#8b949e', fontSize: '0.65rem' }}>
            <span>{formatDate(comp.saleDate)}</span>
            {comp.daysAgo !== undefined && (
              <>
                <span>•</span>
                <span>{comp.daysAgo}d ago</span>
              </>
            )}
            {bedsBaths && (
              <>
                <span>•</span>
                <span>{bedsBaths}</span>
              </>
            )}
            <span>•</span>
            <span>{comp.distanceMiles.toFixed(2)} mi</span>
          </Box>
          {/* Exclusion reason on separate line for excluded comps */}
          {isExcluded && comp.exclusionReason && (
            <Box sx={{ color: '#f87171', fontSize: '0.6rem', mt: 0.25 }}>
              {comp.exclusionReason}
            </Box>
          )}
        </Box>
      </Box>

      {/* Right side: Price, $/sqft */}
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Box sx={{ color: '#f0f6fc', fontSize: '0.75rem', fontWeight: 500 }}>
          {formatCurrency(comp.salePrice)}
        </Box>
        <Box sx={{ color: isExcluded ? '#8b949e' : '#4ade80', fontSize: '0.65rem' }}>
          ${comp.pricePerSqft}/sqft
        </Box>
      </Box>
    </Box>
  );
};

export default ComparablesSection;
